import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CentralEdgeDevice, CentralEdgeModem } from '@simcheck/shared-types';
import { AppShell } from '@/components/layout/AppShell';
import { DeviceHeader } from '../../components/admin/edge/DeviceHeader';
import { DeviceSummaryCard } from '../../components/admin/edge/DeviceSummaryCard';
import { ConnectivityTab } from '@/components/admin/edge/ConnectivityTab';
import { ModemsTab } from '../../components/admin/edge/ModemsTab';
import { ActivityTab } from '../../components/admin/edge/ActivityTab';
import { SettingsTab } from '../../components/admin/edge/SettingsTab';
import { edgeDeviceService } from '../../services/edgeDeviceService';
import { Smartphone, Activity, Settings, HeartPulse, Terminal, Shield, Loader2 } from 'lucide-react';
import { HealthTab } from '../../components/admin/edge/HealthTab';
import { RemoteCommandsPanel } from '../../components/admin/edge/RemoteCommandsPanel';
import { RemoteShellPanel } from '../../components/admin/edge/RemoteShellPanel';
import { AtPolicyAuditTab } from '../../components/admin/edge/AtPolicyAuditTab';
import { useDeviceVisibility, getDeviceTier } from '@/hooks/useDeviceVisibility';
import { useAuth } from '@/contexts/AuthContext';
import { INSTALL_PHASES, getPhaseIndex, formatElapsed } from '@/lib/installPhases';

// Prominent install-progress banner for the device detail page (#106).
// Rendered above the device summary card while installPhase is set.
function InstallProgressBanner({ phase, startedAt }: { phase: string; startedAt?: number }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const idx = getPhaseIndex(phase);
  const entry = idx >= 0 ? INSTALL_PHASES[idx] : { key: phase, label: phase };
  const stepLabel = idx >= 0 ? `Step ${idx + 1}/${INSTALL_PHASES.length}` : 'Installing';
  const elapsedMs = startedAt ? now - startedAt : 0;
  const isAwaitingReboot = entry.key === 'awaiting_reboot';
  const pct = idx >= 0 ? Math.round(((idx + 1) / INSTALL_PHASES.length) * 100) : 0;

  return (
    <div className="mb-4 rounded-lg border border-accent-blue/40 bg-accent-blue-faded/40 dark:bg-accent-blue/10 p-4">
      <div className="flex items-start gap-3">
        <Loader2 className="w-5 h-5 text-accent-light-blue dark:text-accent-blue animate-spin shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-semibold text-foreground">Installing modem-gateway</span>
            <span className="text-xs text-muted-foreground">
              {stepLabel} — {entry.label}
            </span>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {startedAt ? `${formatElapsed(elapsedMs)} on this step` : 'just started'}
            {isAwaitingReboot && ' • press Enter in the SSH terminal to reboot now'}
          </div>
          {/* Step-of-N progress bar — coarse but truthful. The phases aren't
              equal-weight in wall-clock terms (downloads dominate), so this
              is not a real %; it's "where in the sequence we are". */}
          <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-accent-blue transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EdgeDeviceDetail() {
  const { id } = useParams<{ id: string }>();
  const visibilityFlags = useDeviceVisibility();
  const { organizations } = useAuth();
  const [device, setDevice] = useState<CentralEdgeDevice | null>(null);
  const [modems, setModems] = useState<CentralEdgeModem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<'modems' | 'connectivity' | 'health' | 'activity' | 'remote' | 'audit' | 'settings'>(() => {
    // `?tab=connectivity` deep links from the fleet page and alert emails (#252).
    const t = new URLSearchParams(window.location.search).get('tab');
    return (['modems', 'connectivity', 'health', 'activity', 'remote', 'audit', 'settings'] as const).includes(t as any) ? (t as any) : 'modems';
  });
  const [error, setError] = useState<string | null>(null);
  const [activityRefreshTrigger, setActivityRefreshTrigger] = useState(0);

  useEffect(() => {
    if (id) {
      loadDeviceData();
    }
  }, [id]);

  // Auto-poll while the device is mid-install — central-server caches the
  // device list for 5 s, so 5 s polling means the banner updates within
  // ~5-20 s of a phase change. Stops once installPhase clears.
  useEffect(() => {
    if (!device?.installPhase) return;
    const interval = setInterval(() => {
      loadDeviceData();
    }, 5000);
    return () => clearInterval(interval);
  }, [device?.installPhase]);

  // The Connectivity tab shows a LIVE state chip (attaching / attached / down …) read from the
  // modems loaded here. Without a poll it froze at whatever it was when the page opened —
  // "attaching" stayed on screen long after the modem was attached.
  useEffect(() => {
    if (activeTab !== 'connectivity') return;
    const interval = setInterval(() => { refreshModemsSilently(); }, 20000);
    return () => clearInterval(interval);
  }, [activeTab, id]);

  // Modems only, no page-level spinner (that would unmount the open tab every poll).
  const refreshModemsSilently = async () => {
    if (!id) return;
    try { setModems(await edgeDeviceService.getModems(id)); } catch { /* keep the last good state */ }
  };

  const loadDeviceData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      // Unified modems list — live, degraded, and offline — with derived
      // status per entry. The server sources this from the known_modems
      // subcollection so offline modems stay visible until an admin forgets
      // them via the row-level trash button.
      const [deviceData, modemsResp] = await Promise.all([
        edgeDeviceService.getDevice(id),
        edgeDeviceService.getModems(id)
      ]);

      // Tier visibility check — block tenants from deep-linking to demo /
      // lab devices that their org isn't entitled to. Super-admin bypasses
      // (filterDevicesByTier handles this internally).
      if (deviceData) {
        const tier = getDeviceTier(deviceData);
        const allowed =
          visibilityFlags.isSuperAdmin
          || tier === 'production'
          || (tier === 'demo' && visibilityFlags.isDemoOrg)
          || (tier === 'lab' && visibilityFlags.hasLabAccess);
        if (!allowed) {
          setError('Device not found');
          setDevice(null);
          setModems([]);
          return;
        }
      }

      setDevice(deviceData);
      setModems(modemsResp);
    } catch (err) {
      console.error('Error loading device data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load device data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDeviceData();
    setRefreshing(false);
  };

  const handleDeviceUpdate = (updatedDevice: CentralEdgeDevice) => {
    setDevice(updatedDevice);
  };

  const handleCommandSent = () => {
    // Trigger activity tab refresh
    setActivityRefreshTrigger(prev => prev + 1);
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex justify-center items-center min-h-screen">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AppShell>
    );
  }

  if (error || !device) {
    return (
      <AppShell>
        <div className="p-6">
          <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg p-6 text-center">
            <p className="text-error-800 dark:text-error-200 text-lg font-semibold mb-2">Error Loading Device</p>
            <p className="text-error-600 dark:text-error-400">{error || 'Device not found'}</p>
            <button
              onClick={() => window.history.back()}
              className="mt-4 px-4 py-2 bg-error-600 dark:bg-error-500 text-white rounded-md hover:bg-error-700 dark:hover:bg-error-600 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  const tabs = [
    { id: 'modems' as const, label: 'Modems', icon: Smartphone, count: modems?.length || 0 },
    { id: 'connectivity' as const, label: 'Connectivity', icon: Activity, count: modems?.filter((m) => m.connectivityMode).length || undefined },
    { id: 'health' as const, label: 'Health', icon: HeartPulse, count: undefined },
    { id: 'activity' as const, label: 'Activity', icon: Activity, count: undefined },
    { id: 'remote' as const, label: 'Remote', icon: Terminal, count: undefined },
    { id: 'audit' as const, label: 'Audit', icon: Shield, count: undefined },
    { id: 'settings' as const, label: 'Settings', icon: Settings, count: undefined }
  ];

  return (
    <AppShell>
      <div className="p-6">
        {/* Header */}
        <DeviceHeader
        device={device}
        onRefresh={handleRefresh}
        onDeviceUpdate={handleDeviceUpdate}
        refreshing={refreshing}
      />

      {/* Install-progress banner (#106) — only while a release install is
          in flight. Disappears once the updater reports healthy/failed
          and central-server clears installPhase. */}
      {device.installPhase && (
        <InstallProgressBanner
          phase={device.installPhase}
          startedAt={device.installPhaseStartedAt}
        />
      )}

      {/* Device Summary */}
      <DeviceSummaryCard
        device={device}
        modems={modems}
        onCommandSent={handleCommandSent}
      />

      {/* Tabs */}
      <div className="bg-card rounded-lg border border-border dark:border-border-subtle overflow-hidden">
        {/* Tab Navigation */}
        <div className="border-b border-border dark:border-border-subtle">
          <nav className="flex -mb-px">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors
                    ${isActive
                      ? 'border-accent-blue text-accent-light-blue dark:text-accent-blue'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                  {tab.count !== undefined && (
                    <span className={`
                      ml-2 px-2 py-0.5 rounded-full text-xs font-semibold
                      ${isActive ? 'bg-accent-blue-faded text-accent-light-blue dark:text-accent-blue' : 'bg-card text-muted-foreground'}
                    `}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'modems' && (
            <ModemsTab
              modems={modems}
              loading={false}
              deviceId={device.id}
              deviceSkipEsimProvisioning={device.skipEsimProvisioning}
              backhaulApn={device.backhaulApn ?? null}
              deviceOwnerOrgId={device.ownerOrgId ?? null}
              onNetworksUpdated={handleRefresh}
              onModemForgotten={handleRefresh}
            />
          )}
          
          {activeTab === 'connectivity' && (
            <ConnectivityTab deviceId={device.id} modems={modems} onRefreshStatus={refreshModemsSilently} />
          )}

          {activeTab === 'health' && (
            <HealthTab device={device} />
          )}

          {activeTab === 'activity' && (
            <ActivityTab
              deviceId={device.id}
              refreshTrigger={activityRefreshTrigger}
            />
          )}

          {activeTab === 'remote' && (() => {
            const isOrgAdminOfDevice = !!(
              device.ownerOrgId &&
              organizations.some(
                (o) =>
                  o.organizationId === device.ownerOrgId &&
                  (o.role === 'owner' || o.role === 'admin')
              )
            );
            const canSeeShell =
              visibilityFlags.isSuperAdmin ||
              (device.allowOrgAdminShell === true && isOrgAdminOfDevice);
            return (
              <div className="space-y-8">
                <RemoteCommandsPanel deviceId={device.id} />
                {canSeeShell && (
                  <div className="border-t border-border pt-6">
                    <RemoteShellPanel deviceId={device.id} deviceName={device.name} />
                  </div>
                )}
              </div>
            );
          })()}

          {activeTab === 'audit' && (
            <AtPolicyAuditTab deviceId={device.id} />
          )}

          {activeTab === 'settings' && (
            <SettingsTab device={device} onUpdate={handleDeviceUpdate} />
          )}
        </div>
      </div>
      </div>
    </AppShell>
  );
}

